import { useCallback, useMemo, useState } from 'react';
import { useVehicleRouteHistory } from './useVehicleRouteHistory';
import { useRoutePlayback } from './useRoutePlayback';
import { interpolateRoutePosition, detectStops, routeDistanceKm } from '../utils/vehicleHistory';

/**
 * FE-27 — todo lo que necesita una pantalla de histórico de recorrido, en un
 * solo sitio: consulta (FE-25), reproducción y paradas (FE-26) y distancia.
 * Hasta FE-26 esto vivía suelto en `MonitoringPage`; con FE-27 lo usan DOS
 * carcasas — el panel flotante de desktop y la página propia de mobile — y
 * duplicarlo habría sido mantener dos veces la misma lógica.
 *
 * La carcasa decide cómo se ve; esto solo decide qué es verdad.
 */
export function useRouteHistorySession() {
  const { route, isLoading, error, fetchRoute, clear: clearRoute } = useVehicleRouteHistory();
  const [hasQuery, setHasQuery] = useState(false);
  // El vehículo consultado. `TripRoute` no lo trae y el formulario lo guarda en
  // privado; el panel de reproducción lo necesita para mostrar la placa.
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  // "Ubicar vehículo" (FE-27, 2026-09-20): ya no es un salto único sino un modo
  // de seguimiento — el mapa acompaña al vehículo mientras se reproduce. Lo
  // apaga otro clic en el botón, arrastrar el mapa (lo decide el mapa) o limpiar.
  const [following, setFollowing] = useState(false);

  // `Limpiar` deja `route` en `null` y el hook de reproducción reacciona solo
  // (vuelve al inicio y se pausa) sin lógica adicional.
  const playback = useRoutePlayback(route);

  const position = useMemo(
    () => (route ? interpolateRoutePosition(route.points, playback.currentMs) : null),
    [route, playback.currentMs],
  );
  const stops = useMemo(() => (route ? detectStops(route.points) : []), [route]);
  const distanceKm = useMemo(() => (route ? routeDistanceKm(route.points) : null), [route]);

  const query = useCallback((id: string, from: string, to: string) => {
    setHasQuery(true);
    setVehicleId(id);
    setFollowing(false);
    fetchRoute(id, from, to);
  }, [fetchRoute]);

  const clear = useCallback(() => {
    setHasQuery(false);
    setVehicleId(null);
    setFollowing(false);
    clearRoute();
  }, [clearRoute]);

  const toggleFollow = useCallback(() => setFollowing(f => !f), []);
  const stopFollowing = useCallback(() => setFollowing(false), []);

  return { route, isLoading, error, hasQuery, vehicleId, query, clear, playback, position, stops, distanceKm, following, toggleFollow, stopFollowing };
}
